'use client';

import { UiKitInput } from '@components/UiKitInput';
import { UiKitLoadingButton } from '@components/UiKitLoadingButton';
import { observer } from '@legendapp/state/react';
import { useStaticContent } from '@providers/SiteDataContextProvider';
import classNames from 'classnames';
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { customFetchClientSide } from 'src/api/customFetchClientSide';
import { INNER_API_ROUTES } from 'src/constants';
import { useRootStore } from 'src/providers';
import { email, pipe, safeParse, string } from 'valibot';
import styles from './Form.module.css';

const EmailSchema = pipe(
  string('Email is required'),
  email('Incorrect email address'),
);

type TpFormProps = {
  onSetCheckInbox: () => void;
};

export const RecoveryForm = observer((props: TpFormProps) => {
  const { onSetCheckInbox } = props;
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const store$ = useRootStore();

  const staticContent = useStaticContent();

  const isTimerActive = useMemo(() => {
    const dataFromStore = store$.recoveryData.get();
    const userDataFromStorage = Array.isArray(dataFromStore)
      ? dataFromStore
      : [];
    const now = Date.now();

    const existingItem = userDataFromStorage.find(
      (item: { email: string; targetTime: number }) =>
        item.email === email && Number(item.targetTime) > now,
    );

    return !!existingItem;
  }, [store$.recoveryData.get(), email]);

  const startTimerHandle = useCallback(() => {
    const dataFromStore = store$?.recoveryData.get();
    const userDataFromStorage = Array.isArray(dataFromStore)
      ? dataFromStore
      : [];
    const now = Date.now();
    const fiveMinutesMs = 60 * 1000;

    const existingIndex = userDataFromStorage.findIndex(
      (item: { email: string; targetTime: number }) => item.email === email,
    );
    const existingItem =
      existingIndex !== -1 ? userDataFromStorage[existingIndex] : null;

    if (existingItem && Number(existingItem.targetTime) > now) {
      return;
    }

    store$?.recoveryData.set((prevData) => {
      const newEntry = { email, targetTime: now + fiveMinutesMs };
      if (existingIndex !== -1) {
        return [
          ...prevData.slice(0, existingIndex),
          newEntry,
          ...prevData.slice(existingIndex + 1),
        ];
      }
      return [...prevData, newEntry];
    });
  }, [store$?.recoveryData, email]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        setLoading(true);

        await customFetchClientSide(INNER_API_ROUTES.RECOVERY, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        setLoading(false);

        startTimerHandle();
        onSetCheckInbox();
      } catch (err) {
        setLoading(false);
        setError('An error occurred');
      }
    },
    [email, startTimerHandle, onSetCheckInbox],
  );

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (e.target.value.length > 3) {
      const result = safeParse(EmailSchema, e.target.value);

      if (result.issues === undefined) {
        setValidEmail(true);
        setEmailError('');
        return;
      } else {
        const errorMessage = result.issues.find(
          (item) => item.type === 'email',
        )?.message;
        setEmailError(errorMessage || '');
        setError('');
      }
    }
  }, []);

  return (
    <form className={styles.form}>
      <div className={styles.inputRow}>
        <UiKitInput
          isError={emailError !== ''}
          placeholder={
            staticContent.pages.auth.access_recovery.email_placeholder
          }
          fullWidth
          type="email"
          id="emai"
          value={email}
          onChange={handleEmailChange}
          required
        />
        {emailError && <p className={styles.errorMessage}>{emailError}</p>}
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      <div>
        <UiKitLoadingButton
          color="primary"
          className={classNames(!validEmail && styles.submitButton)}
          loading={loading}
          fullWidth
          onClick={handleSubmit}
          disabled={!validEmail || loading || isTimerActive}
        >
          {staticContent.pages.auth.access_recovery.submit_button}
        </UiKitLoadingButton>
      </div>
    </form>
  );
});
